// v3 paving, authored the way age-of-mcp writes it: a flagged sandstone path —
// one mortar bed carrying seven big warm-sandstone slabs in a bold offset
// pattern, edged by a raised kerb on open sides. The library has no terrain, so
// every slab follows FLAT corners (the look the flattened source baked); the
// ground-tile helpers it needs (corners, terrainSurfaceY, warpedSlab,
// warpedPolySlab) are inlined here with the terrain coupling stripped, since the
// lib has no terrain module. Rendered standalone, so all four sides carry a kerb
// (connections = 0). Ported 1:1 from age-of-mcp's v3/paving.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// --- Inlined ground-tile helpers (flat, terrain coupling stripped) -----------
type Corners = readonly [number, number, number, number];
type V2 = readonly [number, number];
type V = readonly [number, number, number];
const FLAT: Corners = [0, 0, 0, 0];
function corners(heights?: readonly number[]): Corners {
  if (!heights || heights.length < 4) return FLAT;
  return [heights[0]!, heights[1]!, heights[2]!, heights[3]!];
}
function surfaceY(lx: number, lz: number, half: number, c: Corners): number {
  const u = (lx / half + 1) / 2;
  const v = (lz / half + 1) / 2;
  const [sw, se, nw, ne] = c;
  return sw * (1 - u) * (1 - v) + se * u * (1 - v) + nw * (1 - u) * v + ne * u * v;
}
function terrainSurfaceY(lx: number, lz: number, half: number, c: Corners): number {
  const u = (lx / half + 1) / 2;
  const v = (lz / half + 1) / 2;
  const [sw, se, nw, ne] = c;
  return u <= v
    ? sw + (ne - nw) * u + (nw - sw) * v
    : sw + (se - sw) * u + (ne - se) * v;
}
function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}
function tri(out: number[], a: V, b: V, c: V): void {
  out.push(...a, ...b, ...c);
}
function signedArea(pts: readonly V2[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x0, z0] = pts[i]!;
    const [x1, z1] = pts[(i + 1) % pts.length]!;
    a += x0 * z1 - x1 * z0;
  }
  return a / 2;
}
function warpedSlab(
  half: number, thickness: number, color: string, c: Corners, lift = 0,
): Part {
  const sw = c[0] + lift, se = c[1] + lift, nw = c[2] + lift, ne = c[3] + lift;
  const yb = Math.min(...c) - thickness;
  const h = half;
  const tSW: V = [-h, sw, -h], tSE: V = [h, se, -h];
  const tNW: V = [-h, nw,  h], tNE: V = [h, ne,  h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb,  h], bNE: V = [h, yb,  h];
  const pos: number[] = [];
  quad(pos, tSW, tNW, tNE, tSE);
  quad(pos, bSW, bSE, bNE, bNW);
  quad(pos, tNW, bNW, bNE, tNE);
  quad(pos, tSE, bSE, bSW, tSW);
  quad(pos, tSE, tNE, bNE, bSE);
  quad(pos, tSW, bSW, bNW, tNW);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}
function warpedPolySlab(
  points: readonly V2[], half: number, thickness: number,
  color: string, c: Corners, lift = 0,
  surfY?: (lx: number, lz: number) => number,
): Part {
  const ring = signedArea(points) > 0 ? [...points].reverse() : [...points];
  const yb = Math.min(...c) - thickness;
  const topY = (p: V2): number => (surfY ? surfY(p[0], p[1]) : surfaceY(p[0], p[1], half, c)) + lift;
  let cx = 0, cz = 0;
  for (const p of ring) { cx += p[0]; cz += p[1]; }
  const pivot: V2 = [cx / ring.length, cz / ring.length];
  const pv: V = [pivot[0], topY(pivot), pivot[1]];
  const pos: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, b = ring[(i + 1) % ring.length]!;
    const ta: V = [a[0], topY(a), a[1]];
    const tb: V = [b[0], topY(b), b[1]];
    const ba: V = [a[0], yb, a[1]];
    const bb: V = [b[0], yb, b[1]];
    tri(pos, pv, ta, tb);
    quad(pos, tb, ta, ba, bb);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// --- Paving (1:1 from the source) --------------------------------------------
const MORTAR = "#4f4439";
const SAND   = ["#cdaa74", "#bd9760", "#d8ba88", "#b28d57"];
const KERB   = "#6b5f4d";

const HALF   = 0.5;
const LIFT   = 0.05;
const BED_H  = 0.16;
const JOINT  = 0.035;
const SLAB_T = 0.10;

const FIELD_IN = 0.385;
const KERB_IN  = 0.41;
const KERB_H   = 0.045;

const E = 0, N_BIT = 2, W = 4, S = 6;

const SQUARE: readonly V2[] = [
  [-HALF, -HALF], [HALF, -HALF], [HALF, HALF], [-HALF, HALF],
];

const ROWS = [-HALF, -0.18, 0.16, HALF] as const;
const COLS: ReadonlyArray<readonly number[]> = [
  [-HALF, -0.06, HALF],
  [-HALF, -0.24, 0.22, HALF],
  [-HALF, 0.10, HALF],
];

function h01(i: number, j: number, salt: number): number {
  const x = Math.sin(i * 127.1 + j * 311.7 + salt * 74.7 + 19.19) * 43758.5453;
  return x - Math.floor(x);
}

type SurfFn = (lx: number, lz: number) => number;

function slabs(
  connections: number, c: Corners, surf: SurfFn, rowCount: number,
): Part[] {
  const has = (bit: number): boolean => (connections & (1 << bit)) !== 0;
  const out: Part[] = [];
  for (let r = 0; r < rowCount; r++) {
    const xs = COLS[r]!;
    for (let i = 0; i + 1 < xs.length; i++) {
      let x0 = xs[i]! + JOINT, x1 = xs[i + 1]! - JOINT;
      let z0 = ROWS[r]! + JOINT, z1 = ROWS[r + 1]! - JOINT;
      if (!has(E)) x1 = Math.min(x1, FIELD_IN);
      if (!has(W)) x0 = Math.max(x0, -FIELD_IN);
      if (!has(N_BIT)) z1 = Math.min(z1, FIELD_IN);
      if (!has(S)) z0 = Math.max(z0, -FIELD_IN);
      const hS = 0.026 + h01(i, r, 17) * 0.012;
      const col = SAND[Math.floor(h01(i, r, 53) * SAND.length) % SAND.length]!;
      const pts: readonly V2[] = [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];
      out.push(warpedPolySlab(pts, HALF, SLAB_T, col, c, LIFT + hS, surf));
    }
  }
  return out;
}

function kerbs(connections: number, c: Corners, surf: SurfFn): Part[] {
  const has = (bit: number): boolean => (connections & (1 << bit)) !== 0;
  const out: Part[] = [];
  const strip = (pts: readonly V2[], h: number): void => {
    out.push(warpedPolySlab(pts, HALF, SLAB_T, KERB, c, LIFT + h, surf));
  };
  if (!has(N_BIT)) strip([[-HALF, KERB_IN], [HALF, KERB_IN], [HALF, HALF], [-HALF, HALF]], KERB_H + 0.004);
  if (!has(S))     strip([[-HALF, -HALF], [HALF, -HALF], [HALF, -KERB_IN], [-HALF, -KERB_IN]], KERB_H + 0.004);
  if (!has(E))     strip([[KERB_IN, -KERB_IN], [HALF, -KERB_IN], [HALF, KERB_IN], [KERB_IN, KERB_IN]], KERB_H);
  if (!has(W))     strip([[-HALF, -KERB_IN], [-KERB_IN, -KERB_IN], [-KERB_IN, KERB_IN], [-HALF, KERB_IN]], KERB_H);
  return out;
}

// The source's stage(), rendered standalone (connections = 0, flat heights). The
// flattened file maps stage 0 -> screed, 1 -> firm bed, 2 -> bed + first slab
// course, complete -> bed + all slabs + kerbs.
function partsFor(s: number, connections = 0, heights?: readonly number[]): Part[] {
  const c = corners(heights);
  if (s <= 0) return [warpedSlab(HALF, 0.08, "#6a5d4c", c, LIFT * 0.4)];
  if (s === 1) return [warpedSlab(HALF, 0.10, "#7a6c58", c, LIFT * 0.7)];
  const surf: SurfFn = (lx, lz) => terrainSurfaceY(lx, lz, HALF, c);
  const bed = warpedPolySlab(SQUARE, HALF, BED_H, MORTAR, c, LIFT, surf);
  if (s === 2) return [bed, ...slabs(connections, c, surf, 1)];
  return [
    bed,
    ...slabs(connections, c, surf, 3),
    ...kerbs(connections, c, surf),
  ];
}

export const pavingV3 = defineModel({
  id: "paving-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Paving (v3)",
  tags: ["building","paving","low-poly","age-of-mcp","v3"],
  summary: "Low-poly paving.",
  description: "A flat, walkable stone tile.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
